import { useEffect, useState } from "react";
import { Check, Edit3, Gauge, Plus, Trash2 } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { ConfirmDialog } from "../components/Modal";
import { api, errorText, inr } from "../lib/api";

const emptyPlan = {
  name: "",
  code: "",
  price: 0,
  duration_days: 30,
  max_students: 0,
  max_staff: 0,
  max_seats: 0,
  max_books: 0,
  features: [],
  status: "ACTIVE",
};

export default function SubscriptionPlans({ user }) {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [featureDraft, setFeatureDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === "MAIN_ADMIN";

  const load = () => {
    api
      .get("/plans")
      .then((response) => setPlans(response.data.data))
      .catch((err) => setError(errorText(err)));
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing({ mode: "new", form: emptyPlan });
    setFeatureDraft("");
    setError("");
  };

  const openEdit = (plan) => {
    setEditing({ mode: "edit", id: plan.id, form: { ...emptyPlan, ...plan, features: plan.features || [] } });
    setFeatureDraft("");
    setError("");
  };

  const addFeature = () => {
    if (!featureDraft.trim()) return;
    setEditing((previous) => ({ ...previous, form: { ...previous.form, features: [...previous.form.features, featureDraft.trim()] } }));
    setFeatureDraft("");
  };

  const removeFeature = (index) => {
    setEditing((previous) => ({ ...previous, form: { ...previous.form, features: previous.form.features.filter((_, idx) => idx !== index) } }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...editing.form,
        price: Number(editing.form.price || 0),
        duration_days: Number(editing.form.duration_days || 1),
        max_students: Number(editing.form.max_students || 0),
        max_staff: Number(editing.form.max_staff || 0),
        max_seats: Number(editing.form.max_seats || 0),
        max_books: Number(editing.form.max_books || 0),
      };
      if (editing.mode === "new") {
        await api.post("/plans", payload);
      } else {
        await api.patch(`/plans/${editing.id}`, payload);
      }
      setEditing(null);
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    try {
      await api.delete(`/plans/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(errorText(err));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="PLATFORM"
        title="Subscription plans"
        description="Create and edit every plan that libraries can be assigned to."
        action={
          isAdmin ? (
            <button className="button button-primary" onClick={openNew} data-testid="add-plan-button">
              <Plus size={16} /> New plan
            </button>
          ) : null
        }
      />
      {error ? <div className="error-message" data-testid="plans-error-message">{error}</div> : null}

      <div className="pricing-grid">
        {plans.map((plan) => (
          <article key={plan.id} className={`pricing-card ${plan.status !== "ACTIVE" ? "pricing-inactive" : ""}`} data-testid={`plan-card-${plan.code}`}>
            <header>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <p className="hero-label">{plan.name.toUpperCase()}</p>
                <StatusPill status={plan.status} />
              </div>
              <h3>{inr(plan.price)}</h3>
              <small>{plan.duration_days} days · {plan.library_count ?? 0} libraries on this plan</small>
            </header>
            <ul>
              <li><Check size={15} /> Up to {plan.max_students || "unlimited"} students</li>
              <li><Check size={15} /> Up to {plan.max_seats || "unlimited"} seats</li>
              <li><Check size={15} /> Up to {plan.max_books || "unlimited"} books</li>
              <li><Check size={15} /> Up to {plan.max_staff || "unlimited"} staff</li>
              {(plan.features || []).map((feature) => (
                <li key={feature}><Check size={15} /> {feature}</li>
              ))}
            </ul>
            {isAdmin ? (
              <div className="plan-actions">
                <button type="button" className="button button-outline button-small" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.code}`}>
                  <Edit3 size={14} /> Edit
                </button>
                <button type="button" className="button button-outline button-small" onClick={() => setConfirmDelete(plan)} data-testid={`delete-plan-${plan.code}`}>
                  <Trash2 size={14} /> Archive
                </button>
              </div>
            ) : null}
          </article>
        ))}
      </div>

      {editing ? (
        <Modal
          title={editing.mode === "new" ? "Create plan" : `Edit ${editing.form.name || "plan"}`}
          eyebrow={editing.mode === "new" ? "NEW PLAN" : "UPDATE PLAN"}
          close={() => setEditing(null)}
          testId="plan-modal"
          wide
        >
          <form className="student-form" onSubmit={submit}>
            {error ? <div className="error-message" data-testid="plan-form-error">{error}</div> : null}
            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">BASICS</span><h3>Plan identity</h3></div>
              <div className="form-grid">
                <label>Name<input required value={editing.form.name} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, name: event.target.value } })} data-testid="plan-name-input" /></label>
                <label>Code (unique)<input required value={editing.form.code} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, code: event.target.value.toUpperCase() } })} data-testid="plan-code-input" placeholder="MONTHLY" /></label>
                <label>Status
                  <select value={editing.form.status} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, status: event.target.value } })} data-testid="plan-status-select">
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </label>
                <label>Price (₹)<input type="number" min="0" step="1" required value={editing.form.price} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, price: event.target.value } })} data-testid="plan-price-input" /></label>
                <label>Duration (days)<input type="number" min="1" required value={editing.form.duration_days} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, duration_days: event.target.value } })} data-testid="plan-duration-input" /></label>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">LIMITS</span><h3>What this plan unlocks (0 = unlimited)</h3></div>
              <div className="form-grid">
                <label>Max students<input type="number" min="0" value={editing.form.max_students} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, max_students: event.target.value } })} data-testid="plan-max-students-input" /></label>
                <label>Max staff<input type="number" min="0" value={editing.form.max_staff} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, max_staff: event.target.value } })} data-testid="plan-max-staff-input" /></label>
                <label>Max seats<input type="number" min="0" value={editing.form.max_seats} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, max_seats: event.target.value } })} data-testid="plan-max-seats-input" /></label>
                <label>Max books<input type="number" min="0" value={editing.form.max_books} onChange={(event) => setEditing({ ...editing, form: { ...editing.form, max_books: event.target.value } })} data-testid="plan-max-books-input" /></label>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">INCLUDED</span><h3>Feature highlights</h3></div>
              <div className="feature-input-row">
                <input value={featureDraft} onChange={(event) => setFeatureDraft(event.target.value)} placeholder="e.g. Priority support" data-testid="plan-feature-input" onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addFeature(); } }} />
                <button type="button" className="button button-outline" onClick={addFeature} data-testid="plan-feature-add-button"><Plus size={14} /> Add</button>
              </div>
              <div className="feature-chip-row" data-testid="plan-feature-chips">
                {editing.form.features.length === 0 ? (
                  <small style={{ color: "#64748b" }}>No feature chips yet. Add bullet points shown to library admins.</small>
                ) : null}
                {editing.form.features.map((feature, index) => (
                  <span key={`${feature}-${index}`} className="feature-chip">
                    {feature}
                    <button type="button" aria-label={`Remove ${feature}`} onClick={() => removeFeature(index)} data-testid={`plan-feature-remove-${index}`}>×</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="modal-actions sticky-actions">
              <button type="button" className="button button-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="button button-primary" disabled={saving} data-testid="save-plan-button">
                {saving ? "Saving…" : editing.mode === "new" ? "Create plan" : "Save changes"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={`Archive ${confirmDelete.name}?`}
          message={`${confirmDelete.library_count ?? 0} libraries are currently on this plan. Active plans become INACTIVE if in use, or deleted if no library is attached.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={archive}
          confirmLabel="Archive plan"
          danger
          testId="archive-plan-dialog"
        />
      ) : null}
    </div>
  );
}
